import type { MartinaEmailComposerReviewReader, MartinaEmailComposerApprovedSend } from "@shared/martinaEmailDraftApproval";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  MARTINA_TURN_CONTEXT_VERSION,
  MartinaGlobalDrawer,
  type MartinaTerminalContext,
  type MartinaTurnRef,
  type MartinaTurnSurface,
} from "@/components/exai/ExAiGlobalDrawer";
import { genericMartinaTerminalTitle, resolveHumanMartinaTerminalContext } from "@/components/exai/martinaHumanTerminalContext";

type EmailReplyDraft = { body: string; mode: "compose" | "reply" | "reply_all" | "forward"; language?: string };
type OpenInput = { context?: MartinaTerminalContext; prompt?: string; autoSubmitPrompt?: boolean; startVoice?: boolean; conversationId?: string; onEmailReplyDraft?: (draft: EmailReplyDraft) => void; onEmailComposerSend?: MartinaEmailComposerApprovedSend; onEmailComposerSave?: (body: string) => Promise<boolean>; getEmailComposerSendReview?: MartinaEmailComposerReviewReader };
type MartinaGlobalContextValue = { openMartina: (input?: OpenInput) => void };
const Context = createContext<MartinaGlobalContextValue | null>(null);
const CONVERSATION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function conversationFromLocation(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const candidate = new URLSearchParams(window.location.search).get("martinaConversation");
  return candidate && CONVERSATION_ID_PATTERN.test(candidate) ? candidate : undefined;
}

function turnRef(type?: string | null, id?: string | number | null, label?: string | null): MartinaTurnRef | undefined {
  if (!type || id == null || String(id).trim() === "") return undefined;
  return { type, id: String(id), ...(label ? { label } : {}) };
}

function inferSurface(context: MartinaTerminalContext): MartinaTurnSurface {
  if (context.surface) return context.surface;
  if (context.composerAssist || context.composeMode) return "composer";
  const route = context.route ?? "";
  if (route === "/feed" || route.startsWith("/feed?")) return "feed";
  if (context.entityType === "martina_room") return "room_drawer";
  if (context.terminalType === "email" || context.entityType === "email_message" || context.entityType === "email_thread" || route.startsWith("/emails")) return "email";
  if (context.terminalType === "calendar" || context.entityType === "calendar" || route === "/calendar") return "calendar";
  if (context.terminalType === "file" || context.entityType === "file" || route.startsWith("/files")) return "file";
  if (context.terminalType === "dashboard" || route === "/" || route === "/opportunities") return "dashboard";
  if (context.terminalType === "entity" || context.entityType) return "entity";
  return "chat";
}

export function inferContextFromLocation(): MartinaTerminalContext | undefined {
  if (typeof window === "undefined") return undefined;
  const route = window.location.pathname;
  if (route === "/feed") {
    return { terminalType: "dashboard", surface: "feed", route, title: "Feed" };
  }
  if (route === "/emails") {
    const params = new URLSearchParams(window.location.search);
    const emailId = params.get("emailId") ?? params.get("messageId");
    const threadId = params.get("threadId");
    if (emailId) {
      const numericThreadId = threadId ? Number(threadId) : null;
      const safeThreadId = numericThreadId !== null && Number.isSafeInteger(numericThreadId) && numericThreadId > 0
        ? numericThreadId
        : null;
      return {
        terminalType: "email",
        surface: "email",
        entityType: "email_message",
        entityId: emailId,
        route: `${route}${window.location.search}`,
        title: "Email",
        threadId: safeThreadId,
      };
    }
    if (threadId) {
      return {
        terminalType: "email",
        surface: "email",
        entityType: "email_thread",
        entityId: threadId,
        route: `${route}${window.location.search}`,
        title: "Conversazione email",
      };
    }
  }
  if (route === "/files") {
    const params = new URLSearchParams(window.location.search);
    const fileId = params.get("fileId");
    const numericFileId = fileId ? Number(fileId) : null;
    const safeFileId = numericFileId !== null && Number.isSafeInteger(numericFileId) && numericFileId > 0
      ? String(numericFileId)
      : null;
    return safeFileId
      ? {
        terminalType: "file",
        surface: "file",
        entityType: "file",
        entityId: safeFileId,
        selectedFileIds: [Number(safeFileId)],
        route: `${route}${window.location.search}`,
        title: "File",
      }
      : { terminalType: "file", surface: "file", route, title: "File" };
  }
  if (route === "/opportunities") {
    return { terminalType: "dashboard", surface: "dashboard", route, title: "Opportunità AI" };
  }
  if (route === "/tasks") {
    const taskId = Number(new URLSearchParams(window.location.search).get("taskId"));
    if (Number.isSafeInteger(taskId) && taskId > 0) {
      return {
        terminalType: "entity",
        surface: "entity",
        entityType: "task",
        entityId: String(taskId),
        route: `/tasks?taskId=${taskId}`,
        title: "Task",
      };
    }
  }
  const match = route.match(/^\/(contacts|companies|deals|leads|branches|meetings|tasks|synergies|emails|brands)\/(\d+)/);
  if (!match) {
    return {
      terminalType: route === "/calendar" ? "calendar" : "global",
      surface: route === "/calendar" ? "calendar" : "chat",
      route,
      title: route === "/calendar" ? "Calendario" : "CRM",
    };
  }
  const entityType = ({
    contacts: "contact",
    companies: "company",
    deals: "deal",
    leads: "lead",
    branches: "branch",
    meetings: "meeting",
    tasks: "task",
    synergies: "synergy",
    emails: "email_message",
    brands: "brand",
  } as Record<string, string>)[match[1]];
  return {
    terminalType: entityType === "email_message" ? "email" : "entity",
    surface: entityType === "email_message" ? "email" : "entity",
    entityType,
    entityId: match[2],
    route,
    title: genericMartinaTerminalTitle(entityType),
  };
}

type MartinaTerminalRelations = NonNullable<MartinaTerminalContext["relations"]>;
type MartinaTerminalRelationScalarKey = {
  [K in keyof MartinaTerminalRelations]-?: NonNullable<MartinaTerminalRelations[K]> extends ReadonlyArray<unknown>
    ? never
    : K;
}[keyof MartinaTerminalRelations];

const ENTITY_RELATION_KEYS: Record<string, Exclude<MartinaTerminalRelationScalarKey, "emailComposerSession">> = {
  contact: "contactId",
  company: "companyId",
  deal: "dealId",
  lead: "leadId",
  branch: "branchId",
  synergy: "synergyId",
  email_message: "emailMessageId",
  email_thread: "emailThreadId",
  task: "taskId",
  meeting: "meetingId",
};

function setScalarRelation(
  relations: MartinaTerminalRelations,
  key: Exclude<MartinaTerminalRelationScalarKey, "emailComposerSession">,
  value: string | number,
) {
  if (relations[key] == null) relations[key] = value;
}

function cleanRelations(context: MartinaTerminalContext): MartinaTerminalContext["relations"] | undefined {
  const relations: MartinaTerminalRelations = { ...(context.relations ?? {}) };
  const directKey = context.entityType ? ENTITY_RELATION_KEYS[context.entityType] : undefined;
  if (directKey && context.entityId) setScalarRelation(relations, directKey, context.entityId);
  if (context.threadId && relations.emailThreadId == null) relations.emailThreadId = context.threadId;
  if (context.selectedFileIds?.length) relations.fileIds = context.selectedFileIds;
  for (const key of Object.keys(relations) as Array<keyof typeof relations>) {
    const value = relations[key];
    if (value == null || (Array.isArray(value) && value.length === 0)) delete relations[key];
  }
  return Object.keys(relations).length ? relations : undefined;
}

function inferOriginSource(context: MartinaTerminalContext): NonNullable<MartinaTerminalContext["origin"]>["source"] {
  if (context.surface === "feed" || context.surface === "room_drawer") return "dashboard";
  if (context.terminalType === "email") return "email";
  if (context.terminalType === "file") return "file";
  if (context.terminalType === "dashboard") return "dashboard";
  if (context.terminalType === "calendar") return "calendar";
  if (context.terminalType === "global") return "global";
  if (context.parentEntityType || context.parentEntityId || context.parent) return "modal";
  return context.entityType ? "record" : "global";
}

function buildOrigin(context: MartinaTerminalContext, relations: MartinaTerminalContext["relations"]): MartinaTerminalContext["origin"] | undefined {
  if (context.origin) return { ...context.origin, relations: context.origin.relations ?? relations };
  const type = context.entityType ?? context.terminalType;
  if (!type) return undefined;
  return {
    type,
    id: context.entityId,
    label: context.title,
    route: context.route,
    source: inferOriginSource(context),
    relations,
  };
}

function explicitRoomId(context: MartinaTerminalContext): string | number | undefined {
  if (context.roomId != null && String(context.roomId).trim() !== "") return context.roomId;
  if (context.entityType === "martina_room" && context.entityId) return context.entityId;
  if (context.primary?.type === "martina_room" && context.primary.id) return context.primary.id;
  return undefined;
}

function omitClientOnlyHints(context: MartinaTerminalContext): MartinaTerminalContext {
  const {
    allowedActions: _allowedActions,
    recentConversation: _recentConversation,
    recentMessages: _recentMessages,
    conversationHistory: _conversationHistory,
    ...rest
  } = context as MartinaTerminalContext & {
    allowedActions?: unknown;
    recentConversation?: unknown;
    recentMessages?: unknown;
    conversationHistory?: unknown;
  };
  return rest;
}

export function buildMartinaRoomTurnContext(input: {
  surface: "feed" | "room_drawer";
  roomId: number;
  title: string;
  route: string;
  relations?: MartinaTerminalContext["relations"];
}): MartinaTerminalContext {
  const id = String(input.roomId);
  return {
    version: MARTINA_TURN_CONTEXT_VERSION,
    surface: input.surface,
    terminalType: "dashboard",
    entityType: "martina_room",
    entityId: id,
    roomId: input.roomId,
    primary: { type: "martina_room", id, label: input.title },
    title: input.surface === "feed" ? `Feed · ${input.title}` : input.title,
    route: input.route,
    relations: input.relations,
  };
}

export function buildMartinaCalendarMeetingTurnContext(input: {
  meetingId: number;
  title: string;
  contact?: { id: number; label?: string } | null;
  company?: { id: number; label?: string } | null;
  deal?: { id: number; label?: string } | null;
}): MartinaTerminalContext {
  const id = String(input.meetingId);
  const meetingRef = { type: "meeting" as const, id, label: input.title };
  const parent = input.contact?.id
    ? turnRef("contact", input.contact.id, input.contact.label)
    : input.company?.id
      ? turnRef("company", input.company.id, input.company.label)
      : input.deal?.id
        ? turnRef("deal", input.deal.id, input.deal.label)
        : undefined;
  return {
    version: MARTINA_TURN_CONTEXT_VERSION,
    surface: "calendar",
    terminalType: "calendar",
    entityType: "meeting",
    entityId: id,
    primary: meetingRef,
    selected: meetingRef,
    title: input.title,
    route: "/calendar",
    ...(parent ? {
      parent,
      parentEntityType: parent.type,
      parentEntityId: parent.id,
      parentTitle: parent.label,
    } : {}),
    relations: {
      meetingId: input.meetingId,
      ...(input.contact?.id ? { contactId: input.contact.id } : {}),
      ...(input.company?.id ? { companyId: input.company.id } : {}),
      ...(input.deal?.id ? { dealId: input.deal.id } : {}),
    },
  };
}

export function normalizeMartinaTerminalContext(context?: MartinaTerminalContext): MartinaTerminalContext | undefined {
  if (!context) return undefined;
  const cleaned = omitClientOnlyHints(context);
  const withTerminalType = cleaned.terminalType
    ? cleaned
    : cleaned.entityType === "calendar" || cleaned.route === "/calendar"
      ? { ...cleaned, terminalType: "calendar" as const }
      : cleaned.entityType === "email_message" || cleaned.entityType === "email_thread" || cleaned.route?.startsWith("/emails")
        ? { ...cleaned, terminalType: "email" as const }
        : cleaned.entityType === "martina_room"
          ? { ...cleaned, terminalType: "dashboard" as const }
          : cleaned.entityType
            ? { ...cleaned, terminalType: "entity" as const }
            : cleaned.route === "/"
              ? { ...cleaned, terminalType: "dashboard" as const }
              : { ...cleaned, terminalType: "global" as const };
  const surface = inferSurface(withTerminalType);
  const primary = withTerminalType.primary
    ?? turnRef(withTerminalType.entityType, withTerminalType.entityId, withTerminalType.title);
  const parent = withTerminalType.parent
    ?? turnRef(withTerminalType.parentEntityType, withTerminalType.parentEntityId, withTerminalType.parentTitle);
  const entityType = withTerminalType.entityType ?? primary?.type;
  const entityId = withTerminalType.entityId ?? primary?.id;
  const roomId = explicitRoomId({ ...withTerminalType, entityType, entityId, primary });
  const withRefs: MartinaTerminalContext = {
    ...withTerminalType,
    version: MARTINA_TURN_CONTEXT_VERSION,
    surface,
    entityType,
    entityId,
    ...(primary ? { primary } : {}),
    ...(withTerminalType.selected ? { selected: withTerminalType.selected } : {}),
    ...(parent ? { parent } : {}),
    ...(roomId != null ? { roomId } : {}),
  };
  const relations = cleanRelations(withRefs);
  const origin = buildOrigin(withRefs, relations);
  return { ...withRefs, ...(relations ? { relations } : {}), ...(origin ? { origin } : {}) };
}

export function MartinaGlobalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [terminalContext, setTerminalContext] = useState<MartinaTerminalContext | undefined>();
  const [initialPrompt, setInitialPrompt] = useState<string | undefined>();
  const [autoSubmitPrompt, setAutoSubmitPrompt] = useState(false);
  const [startVoice, setStartVoice] = useState(false);
  const [preferredConversationId, setPreferredConversationId] = useState<string | undefined>();
  const [emailReplyHandler, setEmailReplyHandler] = useState<((draft: EmailReplyDraft) => void) | undefined>();
  // Invio dell'email aperta nel composer: vive qui perche' il drawer e' montato
  // fuori dal modal e altrimenti non avrebbe modo di raggiungerne il form.
  const [composerSaveHandler, setComposerSaveHandler] = useState<OpenInput['onEmailComposerSave']>();
  const [composerReviewReader, setComposerReviewReader] = useState<MartinaEmailComposerReviewReader | undefined>();
  const [composerSendHandler, setComposerSendHandler] = useState<MartinaEmailComposerApprovedSend | undefined>();
  useEffect(() => {
    if (!open || !terminalContext?.entityType || !terminalContext.entityId) return;
    let cancelled = false;
    void resolveHumanMartinaTerminalContext(terminalContext).then((resolved) => {
      if (!cancelled && resolved.title !== terminalContext.title) setTerminalContext(normalizeMartinaTerminalContext(resolved));
    });
    return () => { cancelled = true; };
  }, [open, terminalContext?.entityType, terminalContext?.entityId, terminalContext?.title]);
  // Una sorgente "proposta" in Stanza apre la conversazione proprietaria, non
  // una home generica. Il parametro e' solo un deep link: non invia comandi e
  // viene consumato per evitare che un refresh riapra un drawer gia' chiuso.
  useEffect(() => {
    const conversationId = conversationFromLocation();
    if (!conversationId) return;
    setTerminalContext(normalizeMartinaTerminalContext(inferContextFromLocation()));
    setInitialPrompt(undefined);
    setAutoSubmitPrompt(false);
    setStartVoice(false);
    setPreferredConversationId(conversationId);
    setEmailReplyHandler(undefined);
    setComposerSendHandler(undefined);
    setComposerReviewReader(undefined);
    setComposerSaveHandler(undefined);
    setOpen(true);
    const params = new URLSearchParams(window.location.search);
    params.delete("martinaConversation");
    const search = params.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`);
  }, []);
  const detachFromSource = () => {
    setTerminalContext(normalizeMartinaTerminalContext({ terminalType: "global", surface: "chat", route: "/", title: "Domanda globale" }));
    setInitialPrompt(undefined);
    setAutoSubmitPrompt(false);
    setStartVoice(false);
    setPreferredConversationId(undefined);
    setEmailReplyHandler(undefined);
    setComposerSendHandler(undefined);
    setComposerReviewReader(undefined);
    setComposerSaveHandler(undefined);
  };
  const value = useMemo(() => ({ openMartina: (input?: OpenInput) => {
    setTerminalContext(normalizeMartinaTerminalContext(input?.context ?? inferContextFromLocation())); setInitialPrompt(input?.prompt); setAutoSubmitPrompt(Boolean(input?.autoSubmitPrompt)); setStartVoice(Boolean(input?.startVoice)); setPreferredConversationId(input?.conversationId); setEmailReplyHandler(() => input?.onEmailReplyDraft); setComposerSendHandler(() => input?.onEmailComposerSend); setComposerReviewReader(() => input?.getEmailComposerSendReview); setComposerSaveHandler(() => input?.onEmailComposerSave); setOpen(true);
  }}), []);
  return <Context.Provider value={value}>{children}<MartinaGlobalDrawer externalOpen={open} onExternalOpenChange={setOpen} terminalContext={terminalContext} onUseGlobalContext={detachFromSource} initialPrompt={initialPrompt} autoSubmitPrompt={autoSubmitPrompt} autoStartVoice={startVoice} preferredConversationId={preferredConversationId} onInsertEmailReply={emailReplyHandler} onSendFromComposer={composerSendHandler} getComposerSendReview={composerReviewReader} onSaveFromComposer={composerSaveHandler} /></Context.Provider>;
}

export function useMartinaGlobal() {
  const value = useContext(Context);
  if (!value) throw new Error("useMartinaGlobal must be used inside MartinaGlobalProvider");
  return value;
}
